import PropTypes from "prop-types";
import React, { useCallback, useEffect, useRef, useState } from "react";

/**
 * Flex-wrap row with vertical separators between same-row items.
 * Separators are always mounted (opacity toggled) so show/hide never
 * changes flex layout and cannot oscillate with wrap recalculation.
 */
const FlexSeparatedRow = ({
  children,
  className,
  itemClassName = "inline-flex",
  separatorClassName = "hr-vertical-1"
}) => {
  const containerRef = useRef(null);
  const itemRefs = useRef([]);
  const [sameRowFlags, setSameRowFlags] = useState([]);

  const items = React.Children.toArray(children).filter(Boolean);

  const recompute = useCallback(() => {
    const flags = [];

    for (let i = 0; i < items.length - 1; i++) {
      const current = itemRefs.current[i];
      const next = itemRefs.current[i + 1];

      if (!current || !next) {
        flags.push(false);
        continue;
      }

      flags.push(current.offsetTop === next.offsetTop);
    }

    setSameRowFlags(prev => {
      if (
        prev.length === flags.length &&
        prev.every((value, index) => value === flags[index])
      ) {
        return prev;
      }

      return flags;
    });
  }, [items.length]);

  useEffect(() => {
    recompute();

    const container = containerRef.current;
    if (!container || typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", recompute);
      return () => window.removeEventListener("resize", recompute);
    }

    const observer = new ResizeObserver(() => {
      recompute();
    });

    observer.observe(container);
    window.addEventListener("resize", recompute);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", recompute);
    };
  }, [recompute]);

  if (items.length === 0) {
    return null;
  }

  return (
    <div ref={containerRef} className={className}>
      {items.map((child, index) => (
        <div key={child.key ?? index} className={itemClassName}>
          <span
            ref={node => {
              itemRefs.current[index] = node;
            }}
            className={"inline-flex"}
          >
            {child}
          </span>
          {index < items.length - 1 ? (
            <span
              className={separatorClassName}
              aria-hidden={"true"}
              style={{
                opacity: sameRowFlags[index] ? 1 : 0
              }}
            />
          ) : null}
        </div>
      ))}
    </div>
  );
};

FlexSeparatedRow.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  itemClassName: PropTypes.string,
  separatorClassName: PropTypes.string
};

export default FlexSeparatedRow;
